import React, { useEffect } from 'react';
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Checkbox,
  Container,
  CssBaseline,
  Grid,
  Toolbar,
  Typography,
  Paper,
} from '@mui/material';
import { useAuth } from '../../context/AuthContext';
import { useParams, useNavigate } from 'react-router-dom';
import { useLazyGetCompanyEmployeeQuery } from '@diditusa/shared/api/companyApi';
import LoadingIndicator from '@diditusa/shared/web_components/LoadingIndicator/loadingIndicator';
import { JobSummary } from '@diditusa/shared/types/companyTypes';
import BasicTable from '@diditusa/shared/web_components/BasicTable/BasicTable';
import PersonIcon from '@mui/icons-material/Person';
import EditIcon from '@mui/icons-material/Edit';
import { JobStatus } from '@diditusa/shared/types/jobTypes';
import { format } from 'date-fns';
import { BasicTableHeader } from '@diditusa/shared/types/commonTypes';

const AgentProfilePage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { agentId } = useParams();
  const companyId = user?.company?.id;
  const [getEmployeeDetails, { data: employee, ...getEmployeeDetailsState }] =
    useLazyGetCompanyEmployeeQuery();

  useEffect(() => {
    if (companyId) {
      getEmployeeDetails({ companyId, employeeId: agentId! });
    }
  }, [companyId]);

  if (getEmployeeDetailsState.isLoading) {
    return <LoadingIndicator isVisible={getEmployeeDetailsState.isLoading} />;
  }

  if (!employee) {
    return <Typography>No details</Typography>;
  }

  const renderJobDetailsTable = () => {
    interface JobDetails {
      id: number;
      reservationCode: string;
      user: string;
      location: string;
      startDate: Date;
      startTime: Date;
      duration: number;
      package: string;
      status: JobStatus;
    }

    type JobDetailsKey = keyof JobDetails;
    const jobDetails: JobDetails[] =
      employee.jobs?.map((j) => ({
        id: j.id,
        reservationCode: j.reservationCode,
        user: `${j.createdBy.firstName} ${j.createdBy.lastName}`,
        location: j.location,
        startTime: j.startTime,
        startDate: j.startTime,
        status: j.status,
        duration: j.durationHours,
        package: j.securityOption?.title,
      })) || [];

    const headers: BasicTableHeader<JobDetailsKey>[] = [
      { key: 'reservationCode', value: 'Reservation Number' },
      { key: 'user', value: 'Customer' },
      { key: 'location', value: 'Location' },
      { key: 'startDate', value: 'Start Date' },
      { key: 'startTime', value: 'Start Time' },
      { key: 'duration', value: 'Duration' },
      { key: 'package', value: 'Package' },
      { key: 'status', value: 'Status' },
    ];

    const getCustomComponentForCell = (field: JobDetailsKey, idx: number) => {
      const c = jobDetails[idx];
      if (field === 'id') {
        return <Button onClick={() => navigate(`/company/jobs/${c.id}`)}>{c.id}</Button>;
      }
      if (field === 'startDate') {
        return format(c.startDate, 'MM/dd/yyyy');
      }

      if (field === 'startTime') {
        return format(c.startTime, 'hh:mm a');
      }
    };

    return (
      <Box sx={{ mb: 3 }}>
        <BasicTable
          headers={headers}
          data={jobDetails}
          getCustomComponentForField={getCustomComponentForCell}
        />
      </Box>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        backgroundColor: 'white',
        textAlign: 'start',
      }}
    >
      <Container sx={{ py: 5, flexGrow: 1, maxWidth: '960px' }}>
        <Box display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
          <Typography
            variant='h4'
            fontWeight='bold'
            color='#111418'
            gutterBottom
            sx={{ width: 'fit-content' }}
          >
            Agent Profile
          </Typography>
          <Button sx={{ color: '#111418' }} onClick={() => navigate('edit')}>
            <EditIcon />
          </Button>
        </Box>

        {/* Profile Section */}
        <Grid container spacing={4} alignItems='center'>
          <Grid>
            {employee.imageUrl ? (
              <Avatar src={employee.imageUrl} sx={{ width: 128, height: 128 }} />
            ) : (
              <PersonIcon sx={{ height: 75, width: 75 }} />
            )}
          </Grid>
          <Grid>
            <Typography variant='h5' fontWeight='bold' color='#111418'>
              {`${employee.firstName} ${employee.lastName}`}
            </Typography>
          </Grid>
        </Grid>

        {/* Sections */}
        <Box mt={4}>
          <Section
            title='Contact Information'
            fields={{
              Email: employee.email,
              'Phone Number': employee.phoneNumber || '-',
            }}
          />

          <Section
            title='Personal Details'
            fields={{
              'Date of Birth': employee.dob?.toString() || '-',
              Height: employee.height || '-',
              Weight: employee.weight || '-',
            }}
          />

          <Typography variant='h6' fontWeight='bold' color='#111418' mt={4} mb={2}>
            Biography
          </Typography>
          <Typography color='#111418' paragraph>
            {employee.bio || 'No bio added'}
          </Typography>

          <Typography variant='h6' fontWeight='bold' color='#111418' mt={4} mb={2}>
            Licenses & Certifications
          </Typography>
          <Box>
            <ul>
              {employee.licenses?.map((license: string) => (
                <Box key={license} display='flex' alignItems='center' py={1}>
                  <li color='primary'>
                    <Typography>{license}</Typography>
                  </li>
                </Box>
              )) || <Typography>No licenses added</Typography>}
            </ul>
          </Box>

          <Typography variant='h6' fontWeight='bold' color='#111418' mt={4} mb={2}>
            Job Summary
          </Typography>
          {renderJobDetailsTable()}
        </Box>
      </Container>
    </Box>
  );
};

const Section: React.FC<{ title: string; fields: Record<string, string> }> = ({
  title,
  fields,
}) => {
  return (
    <>
      <title>Agent Details</title>
      <Box mt={4}>
        <Typography variant='h6' fontWeight='bold' color='#111418' mb={2}>
          {title}
        </Typography>
        {Object.entries(fields).map(([label, value]) => (
          <Grid container key={label} sx={{ py: 1, borderTop: '1px solid #dce0e5' }}>
            <Grid size={{ xs: 4 }}>
              <Typography color='#637588' fontSize='14px'>
                {label}
              </Typography>
            </Grid>
            <Grid size={{ xs: 8 }}>
              <Typography color='#111418' fontSize='14px'>
                {value}
              </Typography>
            </Grid>
          </Grid>
        ))}
      </Box>
    </>
  );
};

export default AgentProfilePage;
